/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import { Injectable } from '@angular/core';
import { NgxPermissionsService } from "ngx-permissions";
import { HttpClient } from "@angular/common/http";
import { environment } from "@environments/environment";
import { parseParams } from "@shared/utils";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";

import { ROLES } from '@shared/models/user';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  /**
   * Thiết lập quyền mặc định dựa trên Role ID (SuperAdmin, Admin, Member...)
   * @param roleId ID của Role từ AuthService.auth.role
   */
  setPermissionsByRole(roleId: number | undefined): void {
    if (roleId === undefined) {
      this.clearPermissions();
      return;
    }

    let permissions: string[] = [];

    // Ánh xạ từ ID sang chuỗi quyền (có thể tùy chỉnh theo yêu cầu thực tế của dự án)
    if (roleId === ROLES.SuperAdmin) {
      permissions = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'MEMBER'];
    } else if (roleId === ROLES.Admin) {
      permissions = ['ADMIN', 'MANAGER', 'MEMBER'];
    } else if (roleId === ROLES.Business_Manager) {
      permissions = ['MANAGER', 'MEMBER'];
    } else if (roleId === ROLES.Business_Staff || roleId === ROLES.Member) {
      permissions = ['MEMBER'];
    }

    // Nạp quyền vào Subject và Thư viện
    this.permissions.next(permissions);
    this.ngxPermissions.loadPermissions(permissions);
    this.lastLoadedKey = `static_role_${roleId}`;
  }
  /**
   * Lưu trữ danh sách quyền hiện tại của user
   */
  private permissions = new BehaviorSubject<string[]>([]);
  public permissions$ = this.permissions.asObservable();

  /**
   * Cache tạm thời cho các request đang chạy (tránh call trùng lặp khi nhiều bộ phận cùng yêu cầu)
   */
  private loadingCache = new Map<string, Observable<string[]>>();

  /**
   * Bộ nhớ đệm lưu Key (roleId_businessId) của lần nạp quyền thành công gần nhất.
   * Giúp hệ thống biết có cần nạp lại hay không khi người dùng chuyển hướng giữa các trang.
   */
  private lastLoadedKey = '';

  /**
   * Lưu ý QUAN TRỌNG VỀ KIẾN TRÚC:
   * Không được Inject ApiService vào đây để tránh lỗi Vòng lặp phụ thuộc (Circular Dependency - NG0200).
   * Nguyên nhân: ApiService -> AuthService -> PermissionService -> ApiService.
   * Do đó, chúng ta bắt buộc sử dụng trực tiếp HttpClient của Angular để thực hiện gọi API lấy quyền.
   */
  constructor(

    private ngxPermissions: NgxPermissionsService,
    private http: HttpClient,
  ) {
  }

  /**
   * Load permissions cho custom role từ API.
   * @param roleId ID của Role cần lấy quyền
   * @param businessId ID của Doanh nghiệp tương ứng
   * @returns Observable chứa danh sách quyền
   */
  loadRolePermissions(roleId: string, businessId: number): Observable<string[]> {
    const cacheKey = `role_${roleId}_business_${businessId}`;


    // 1. Kiểm tra nếu yêu cầu trùng khớp với dữ liệu đã nạp thành công trước đó
    // Và danh sách quyền hiện tại không rỗng thì trả về luôn, không gọi API.
    if (this.lastLoadedKey === cacheKey && this.permissions.value.length > 0) {
      return of(this.permissions.value);
    }

    // 2. Chống gọi trùng lặp nếu request đang được thực thi (đang bay trên mạng)
    if (this.loadingCache.has(cacheKey)) {
      return this.loadingCache.get(cacheKey)!;
    }

    const requestParams = {
      businessId: businessId,
      roleId: roleId,
    };

    const params = parseParams(requestParams);

    // 3. Thực hiện gọi API lấy chi tiết role và quyền
    const loadingObservable = this.http.post<any>(`${environment.baseUrlApi}/business/role/detail`, params).pipe(
      map(res => {
        if (res.errorCode) {
          throw new Error(res.messages?.[0] || 'Error getting permissions');
        }

        // Parse danh sách quyền từ response API
        const roleData = res.items && res.items.length > 0 ? res.items[0] : res;
        
        // Hỗ trợ nhiều cấu trúc dữ liệu khác nhau từ BE:
        const rawPermissions = roleData?.permissions || 
                            roleData?.role?.permissions || 
                            res?.data?.role?.permissions || 
                            res?.data?.permissions || 
                            [];
        
        // Chuẩn hóa quyền: chuyển về string và trim khoảng trắng
        const permissions = Array.isArray(rawPermissions) 
                            ? rawPermissions.map(p => String(p).trim()) 
                            : [];

        // Lưu vào subject để các component (như NavService) cập nhật Menu động
        this.permissions.next(permissions);

        // Nạp vào thư viện ngxPermissions để phục vụ các Guard/Directive
        this.ngxPermissions.loadPermissions(permissions);

        // Đánh dấu đã nạp thành công Business/Role này
        this.lastLoadedKey = cacheKey;

        return permissions;
      }),
      tap(() => {
        // Xóa khỏi cache đang chạy sau khi hoàn tất
        this.loadingCache.delete(cacheKey);
      }),
      catchError(error => {
        // Nếu lỗi thì xóa trắng quyền và cache
        this.clearPermissions();
        this.loadingCache.delete(cacheKey);
        return of([]);
      }),
      shareReplay(1) // Đảm bảo các subscriber cùng thời điểm dùng chung 1 request
    );

    this.loadingCache.set(cacheKey, loadingObservable);
    return loadingObservable;
  }

  /**
   * Xóa toàn bộ quyền hiện tại và reset bộ nhớ đệm.
   * Thường dùng khi đăng xuất hoặc vào các trang công cộng (Public layout).
   */
  clearPermissions(): void {
    this.permissions.next([]);
    this.ngxPermissions.loadPermissions([]);
    this.lastLoadedKey = ''; // Quan trọng: Reset cache key để lần truy cập private sau sẽ nạp lại
  }

  /**
   * Lấy quyền hiện tại
   */
  getCurrentPermissions(): string[] {
    return this.permissions.value;
  }

  /**
   * Kiểm tra xem role hiện tại có cờ permission này không
   */
  hasPermission(permission: string): boolean {
    return this.permissions.value.includes(permission);
  }
}
